import { CURRENT_CLIENT } from '@/constants'
import type {
  ClientLayout,
  SidebarBrandConfig,
  SidebarBrandSize,
} from '@/types/theme'

/** Hit target / mark size. Sized to leave inset inside the icon rail (see rail width). */
const HIT_SIZE_CLASS: Record<SidebarBrandSize, string> = {
  md: 'size-8', // 32px — fits default 3rem rail + p-2
  lg: 'size-10', // 40px — needs widened rail
  xl: 'size-12', // 48px — needs widened rail
}

/** Lucide / favicon img size inside the hit target when not filling it. */
const GLYPH_SIZE_CLASS: Record<SidebarBrandSize, string> = {
  md: 'size-4',
  lg: 'size-5',
  xl: 'size-6',
}

/** Icon-rail width so large unboxed marks keep breathing room (default SIDEBAR_WIDTH_ICON is 3rem). */
const RAIL_WIDTH: Record<SidebarBrandSize, string | undefined> = {
  md: undefined,
  lg: '4rem',
  xl: '4.5rem',
}

export function getSidebarBrandConfig(): SidebarBrandConfig {
  return (
    (CURRENT_CLIENT as { layout?: ClientLayout }).layout?.sidebarBrand ?? {}
  )
}

export function resolveSidebarBrandSize(
  size: SidebarBrandSize | undefined,
): SidebarBrandSize {
  return size === 'lg' || size === 'xl' ? size : 'md'
}

export function sidebarBrandHitClass(size: SidebarBrandSize | undefined): string {
  return HIT_SIZE_CLASS[resolveSidebarBrandSize(size)]
}

export function sidebarBrandGlyphClass(size: SidebarBrandSize | undefined): string {
  return GLYPH_SIZE_CLASS[resolveSidebarBrandSize(size)]
}

/** Full mark (e.g. yeshumanMark) fills the hit target. */
export function sidebarBrandMarkClass(size: SidebarBrandSize | undefined): string {
  return HIT_SIZE_CLASS[resolveSidebarBrandSize(size)]
}

export function sidebarBrandIsBoxed(config: SidebarBrandConfig): boolean {
  return config.boxed !== false
}

/**
 * Wider icon rail for large unboxed marks so they are not flush to the chrome.
 * Boxed / md keep the platform default (3rem).
 * App chrome no longer applies this — collapsed rail stays 3rem so every tenant
 * shares the same 32px glyph column (see SIDEBAR_GLYPH_SLOT_CLASS).
 */
export function sidebarBrandIconRailWidth(
  config: SidebarBrandConfig,
): string | undefined {
  if (sidebarBrandIsBoxed(config)) return undefined
  return RAIL_WIDTH[resolveSidebarBrandSize(config.size)]
}
